import React, { useState } from 'react';
import { Autocomplete, Box, Button, Collapse, Grid, Paper, TextField, Typography } from '@mui/material';
import {
  FilterAltOffOutlined as FilterAltOffOutlinedIcon,
  FilterAltOutlined as FilterAltOutlinedIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import {
  partnerFilterButtonSx as filterButtonSx,
  partnerFilterFieldSx as filterFieldSx,
} from '../Parterns/partnerListTheme';

const DashboardTimelineFilters = ({
  contractRange,
  onContractChange,
  evaluationRange,
  onEvaluationChange,
  financements = [],
  selectedFinancement,
  onFinancementChange,
  importedPeriods = [],
  selectedImportedPeriodId,
  onImportedPeriodChange,
  onClear,
}) => {
  const [filtersVisible, setFiltersVisible] = useState(true);
  const selectedImportedPeriod = importedPeriods.find(
    (period) => String(period.value) === String(selectedImportedPeriodId)
  ) || null;
  const selectedFinancementOption = financements.find(
    (financement) => String(financement.value) === String(selectedFinancement)
  ) || null;
  const hasActiveFilters = Boolean(
    contractRange.from || contractRange.to || evaluationRange.from || evaluationRange.to
      || selectedFinancement || selectedImportedPeriodId
  );

  const dateField = (value, onChange) => (
    <TextField
      type="date"
      size="small"
      variant="outlined"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      sx={{ ...filterFieldSx, width: { xs: '100%', sm: 165 } }}
      InputLabelProps={{ shrink: true }}
    />
  );

  return (
    <Paper elevation={0} sx={{ border: '1px solid #cbd8e6', borderRadius: '8px', p: 1.25, mb: 1.75 }}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          justifyContent: 'space-between',
          mb: filtersVisible ? 1.5 : 0,
        }}
      >
        <Typography color="#52677c" fontSize={10} fontWeight={700} letterSpacing="0.05em">
          FILTRES
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'flex-end',
            ml: { sm: 'auto' },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {filtersVisible && (
            <Button
              color="inherit"
              disabled={!hasActiveFilters}
              onClick={onClear}
              size="small"
              startIcon={<RestartAltIcon />}
              sx={{
                ...filterButtonSx,
                borderColor: '#9fb1c3',
                color: '#173d61',
                '&:hover': { backgroundColor: '#f3f7fa', borderColor: '#173d61' },
              }}
              variant="outlined"
            >
              Réinitialiser
            </Button>
          )}
          <Button
            color="inherit"
            onClick={() => setFiltersVisible((visible) => !visible)}
            size="small"
            startIcon={filtersVisible ? <FilterAltOffOutlinedIcon /> : <FilterAltOutlinedIcon />}
            sx={{
              ...filterButtonSx,
              borderColor: '#9fb1c3',
              color: '#173d61',
              '&:hover': { backgroundColor: '#f3f7fa', borderColor: '#173d61' },
            }}
            variant="outlined"
          >
            {filtersVisible ? 'Masquer filtres' : 'Afficher filtres'}
          </Button>
        </Box>
      </Box>

      <Collapse in={filtersVisible} timeout="auto" unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Grid container spacing={1.25}>
            <Grid item xs={12} sm={6} md={6}>
              <Autocomplete
                size="small"
                options={financements}
                value={selectedFinancementOption}
                onChange={(_, option) => onFinancementChange(option?.value || '')}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
                noOptionsText="Aucun financement"
                renderInput={(params) => (
                  <TextField {...params} label="Financement" size="small" sx={filterFieldSx} variant="outlined" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Autocomplete
                size="small"
                options={importedPeriods}
                value={selectedImportedPeriod}
                onChange={(_, option) => onImportedPeriodChange(option?.value || '')}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
                noOptionsText="Aucune période synchronisée"
                renderInput={(params) => (
                  <TextField {...params} label="Base importée" size="small" sx={filterFieldSx} variant="outlined" />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ alignItems: 'stretch', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 1.25 }}>
            <Box sx={{ alignItems: 'center', border: '1px solid #d5e0eb', borderRadius: '6px', display: 'flex', flex: 1, flexWrap: 'wrap', gap: 1, p: 1, width: '100%' }}>
              <Typography color="#52677c" fontSize={11} fontWeight={700}>Période du contrat :</Typography>
              {dateField(contractRange.from, (value) => onContractChange('from', value))}
              <Typography color="#52677c" fontSize={12}>→</Typography>
              {dateField(contractRange.to, (value) => onContractChange('to', value))}
            </Box>
            <Box sx={{ alignItems: 'center', border: '1px solid #d5e0eb', borderRadius: '6px', display: 'flex', flex: 1, flexWrap: 'wrap', gap: 1, p: 1, width: '100%' }}>
              <Typography color="#52677c" fontSize={11} fontWeight={700}>Période de l'évaluation :</Typography>
              {dateField(evaluationRange.from, (value) => onEvaluationChange('from', value))}
              <Typography color="#52677c" fontSize={12}>→</Typography>
              {dateField(evaluationRange.to, (value) => onEvaluationChange('to', value))}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default DashboardTimelineFilters;
